'use client';

import { Url as NextUrl } from 'next/dist/shared/lib/router/router';
import { useRouter } from 'next/navigation';
import { createContext, useMemo } from 'react';

import { type LinkProps } from '../atoms/link/link.tsx';

export type Url = NextUrl;

export type RouterContextProps = {
  router: ReturnType<typeof useRouter>;
  RouterComponent: React.ComponentType<LinkProps>;
  usePathname: () => string;
};
/**
 * Router context responsible for providing routing
 * information/functionality from the consuming app.
 * For now all of our routing functionality will copy
 * and have the same API as NextJS.
 *
 * @TODO if needed we will need to support other routing systems and ensure we
 * can leverage their functionality within our normalized API.
 */
export const RouterContext = createContext<RouterContextProps | null>(null);

/**
 * Provider to be imported by the consuming app where it can
 * provide its routing functionality.
 */
export type RouterProviderProps = React.PropsWithChildren & {
  routerData: RouterContextProps;
};
export const RouterProvider: React.FC<RouterProviderProps> = ({
  children,
  routerData: { router, RouterComponent, usePathname },
}) => {
  const routerData = useMemo(() => {
    return {
      router,
      usePathname,
      RouterComponent,
    };
    // eslint-disable-next-line react-compiler/react-compiler
  }, [JSON.stringify(router), RouterComponent, usePathname]);

  return (
    <RouterContext.Provider value={routerData}>
      {children}
    </RouterContext.Provider>
  );
};
